import { useQuery } from '@tanstack/react-query'
import { useApiClientUser } from '~/@api/client/useApiClientUser'
import { useUser } from '~/@biz_account/useUser/useUser'
import {
  calculateRevenueFeeNettoAmount,
  calculateRevenueFeeNettoAmountUnrounded,
} from '../revenueFee.utils'

export function useRevenueFee(alwaysApplyRevenueFee: boolean = false) {
  const client = useApiClientUser()
  const { user } = useUser()

  async function fetchRevenueFee() {
    return client.user
      .settingsControllerGetRevenueFeeSetting({ secure: true })
      .then((res) => res.data.data)
  }

  const {
    data: revenueFeeSetting,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['revenueFee'],
    queryFn: fetchRevenueFee,
  })

  const revenueFee = (() => {
    if (isLoading || isError || !revenueFeeSetting) return 0

    if (!revenueFeeSetting.enabled) return 0
    if (
      revenueFeeSetting.validFrom &&
      new Date(revenueFeeSetting.validFrom) > new Date()
    )
      return 0

    if (alwaysApplyRevenueFee) return revenueFeeSetting?.fee

    if (user?.hasRevenueFeeImmunity) return 0

    return revenueFeeSetting?.fee
  })()

  function calculateFeeNettoAmount(amount: number) {
    return calculateRevenueFeeNettoAmount({
      fee: revenueFee,
      revenueAmount: amount,
    })
  }

  function calculateFeeNettoAmountUnrounded(amount: number) {
    return calculateRevenueFeeNettoAmountUnrounded({
      fee: revenueFee,
      revenueAmount: amount,
    })
  }

  return {
    revenueFeeSetting,
    fee: revenueFeeSetting?.fee ?? 0,
    isReady: !(isLoading || isError),
    calculateFeeNettoAmount,
    calculateFeeNettoAmountUnrounded,
  }
}
